import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { StorybookStatusProvider } from './context/StorybookStatusContext';
import { DashboardLayout } from './components/layout/DashboardLayout';
import Dashboard from './pages/Dashboard';
import LearningIntelligenceEngine from './pages/LearningIntelligenceEngine';
import LIEStylePreviewPage from './pages/LIEStylePreviewPage';
import ConfigurationPage from './pages/ConfigurationPage';
import PortfolioAnalysisPage from './pages/PortfolioAnalysisPage';
import FullMatrixPage from './pages/FullMatrixPage';
import ResultsPage from './pages/ResultsPage';
import ReportsPage from './pages/ReportsPage';
import ImportPage from './pages/ImportPage';
import ScenariosPage from './pages/ScenariosPage';
import LEIReferencePage from './pages/LEIReferencePage';
import BenchmarksReferencePage from './pages/BenchmarksReferencePage';
import CompareScenarios from './pages/CompareScenarios';
import DampeningComparisonPage from './pages/DampeningComparisonPage';
import ComparisonAnalyticsPage from './pages/ComparisonAnalyticsPage';
import GoalsPage from './pages/GoalsPage';
import CustomMeasuresPage from './pages/CustomMeasuresPage';
import ScheduledReportsPage from './pages/ScheduledReportsPage';
import AccuracyLeaderboardPage from './pages/AccuracyLeaderboardPage';
import ProspectusBuilderPage from './pages/ProspectusBuilderPage';
import ProspectusListPage from './pages/ProspectusListPage';
import ProspectusComparePage from './pages/ProspectusComparePage';
import NarrativeReportsPage from './pages/NarrativeReportsPage';
import BoardReadyPage from './pages/BoardReadyPage';
import DataManagementPage from './pages/DataManagementPage';
import DataManagementHub from './pages/DataManagementHub';
import BenchmarkBaselinePage from './pages/BenchmarkBaselinePage';
import GapAnalysisPage from './pages/GapAnalysisPage';
import SolutionFinderPage from './pages/SolutionFinderPage';
import LEIDashboardPage from './pages/LEIDashboardPage';
import BusinessCaseBuilderPage from './pages/BusinessCaseBuilderPage';
import UsageAnalyticsPage from './pages/UsageAnalyticsPage';
import ResearchEvidencePage from './pages/ResearchEvidencePage';
import ComponentDataImportPage from './pages/ComponentDataImportPage';
import SegmentsPage from './pages/SegmentsPage';
import ProgramAnalyticsPage from './pages/ProgramAnalyticsPage';
import BenchmarkingPage from './pages/BenchmarkingPage';
import OrgBenchmarksPage from './pages/OrgBenchmarksPage';
import NotificationPreferencesPage from './pages/NotificationPreferencesPage';
import NotificationCenterPage from './pages/NotificationCenterPage';
import AlertRulesPage from './pages/AlertRulesPage';
import LEIHealthDashboardPage from './pages/LEIHealthDashboardPage';
import ScoreTrendsPage from './pages/ScoreTrendsPage';
import BoardBriefPage from './pages/BoardBriefPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminTelemetryPage from './pages/AdminTelemetryPage';
import ReleaseNotesAdminPage from './pages/ReleaseNotesAdminPage';
import AIReportBuilderPage from './pages/admin/AIReportBuilderPage';
import AdminGlossaryPage from './pages/admin/AdminGlossaryPage';
import AdminMessagingPage from './pages/admin/AdminMessagingPage';
import NotFoundPage from './pages/NotFoundPage';
import DataHealthPage from './pages/DataHealthPage';
import CodeHealthPage from './pages/CodeHealthPage';
import EmailSuppressionsAdminPage from './pages/EmailSuppressionsAdminPage';
import CollateralLibraryPage from './pages/CollateralLibraryPage';
import IndustryLibraryPage from './pages/IndustryLibraryPage';
import CoefficientHistoryPage from './pages/CoefficientHistoryPage';
import OnboardingWizardPage from './pages/OnboardingWizardPage';
import OnboardingListPage from './pages/OnboardingListPage';
import PEHealthPage from './pages/people-engine/PEHealthPage';
import PEDiagnosticPage from './pages/people-engine/PEDiagnosticPage';
import PEGoalsPage from './pages/people-engine/PEGoalsPage';
import InterventionBacklogPage from './pages/people-engine/InterventionBacklogPage';
import PEReferencePage from './pages/people-engine/PEReferencePage';
import PEBenchmarkingPage from './pages/people-engine/PEBenchmarkingPage';
import CorrelationAnalysisPage from './pages/CorrelationAnalysisPage';
import MaturityHealthPage from './pages/MaturityHealthPage';
import ROEHealthPage from './pages/ROEHealthPage';
import PerformanceHealthPage from './pages/PerformanceHealthPage';
import ROIHealthPage from './pages/ROIHealthPage';
import StoryGuidePage from './pages/StoryGuidePage';
import CapabilityInventoryPage from './pages/CapabilityInventoryPage';
import AnomalyDashboardPage from './pages/AnomalyDashboardPage';
import ROEModellerPage from './pages/ROEModellerPage';
import ROEReferencePage from './pages/ROEReferencePage';
import MaturityReferencePage from './pages/MaturityReferencePage';
import TransferMethodologyPage from './pages/TransferMethodologyPage';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
// RegisterPage import removed — self-service signup is disabled (admins
// provision accounts). The /register route now redirects to /login.
import ProfilePage from './pages/ProfilePage';
import OnboardingWizard from './pages/OnboardingWizard';
import DemoPreviewPage from './pages/DemoPreviewPage';
import ReportCardSharePage from './pages/ReportCardSharePage';
import ProspectusSharePage from './pages/ProspectusSharePage';
import CollateralBundleSharePage from './pages/CollateralBundleSharePage';
import QuarterlyDeckPublicPage from './pages/QuarterlyDeckPublicPage';
import PIELandingPage from './pages/personal-impact/PIELandingPage';
import PIEDashboardPage from './pages/personal-impact/PIEDashboardPage';
import PIEImprovePage from './pages/personal-impact/PIEImprovePage';
import PIESimulatorPage from './pages/personal-impact/PIESimulatorPage';
import PIECoachPage from './pages/personal-impact/PIECoachPage';
import FrontlineDemoPage from './pages/personal-impact/frontline/FrontlineDemoPage';
import DemoTagsPage from './pages/personal-impact/frontline/DemoTagsPage';
import TourRemotePage from './pages/personal-impact/frontline/TourRemotePage';
import PIEValidatePage from './pages/personal-impact/PIEValidatePage';
import PIETeamPage from './pages/personal-impact/PIETeamPage';
import PIEPulseHistoryPage from './pages/personal-impact/PIEPulseHistoryPage';
import PIEManagerPulsePage from './pages/personal-impact/PIEManagerPulsePage';
import PIECalibrationReviewPage from './pages/personal-impact/PIECalibrationReviewPage';
import { useVersionCheck } from './hooks/useVersionCheck';

// Protected Route wrapper — redirects to /onboarding if needed
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, needsOnboarding, isSuperAdmin, mustChangePassword } = useAuth();
  const location = useLocation();
  useVersionCheck();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Forced password rotation always wins — admin-created accounts
  // can't touch the rest of the app until they've chosen their own
  // password. Skip when already on /change-password to avoid a loop.
  if (mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Org Admins who haven't completed onboarding get redirected.
  // Super Admins are never trapped.
  if (needsOnboarding && !isSuperAdmin) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

// Admin Route wrapper - requires org_admin or super_admin
const AdminRoute = ({ children }) => {
  const { isOrgAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }
  
  if (!isOrgAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Super-admin-only route wrapper
const SuperAdminRoute = ({ children }) => {
  const { isSuperAdmin, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }
  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Onboarding Route wrapper — only accessible when onboarding is needed
const OnboardingRoute = () => {
  const { isAuthenticated, loading, needsOnboarding, isSuperAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If onboarding is already complete (or Super Admin), go to dashboard
  if (!needsOnboarding || isSuperAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return <OnboardingWizard />;
};

function AppRoutes() {
  const location = useLocation();

  // CRITICAL (Emergent Auth playbook): handle the OAuth callback SYNCHRONOUSLY
  // during render — before route matching — so the session_id is consumed exactly
  // once and no other component (LoginPage, ProtectedRoute) can race against it.
  // This was required to fix a redirect loop on iPad / iOS Safari.
  const oauthHash =
    (location.hash || (typeof window !== 'undefined' ? window.location.hash : '') || '');
  if (oauthHash.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Hostname-based default entry point. When the app is reached via a
  // `mirror.*` subdomain (e.g. mirror.seertech.ai), the bare root `/`
  // should land users straight in the Mirror experience instead of
  // Mission Control. Only the EXACT root path is redirected, so every
  // /mirror/* route, deep links, and the primary `mc.*` host are all
  // left untouched. (Emergent maps one host per deployment, so in
  // practice this fires when a DNS redirect / second deployment serves
  // the app on the mirror subdomain.)
  const isMirrorHost =
    typeof window !== 'undefined' &&
    /^mirror\./i.test(window.location.hostname);
  if (isMirrorHost && location.pathname === '/') {
    return <Navigate to="/mirror" replace />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      {/*
        Self-service registration is disabled — only Super Admins and
        Org Admins create accounts (Admin Dashboard → Create User).
        Any deep-link to /register bounces straight back to /login so
        the policy is impossible to side-step from the URL bar.
      */}
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/demo-preview/:token" element={<DemoPreviewPage />} />
      <Route path="/share/report-card/:token" element={<ReportCardSharePage />} />
      <Route path="/prospectus/share/:token" element={<ProspectusSharePage />} />
      <Route path="/share/bundle/:token" element={<CollateralBundleSharePage />} />
      <Route path="/q/:token" element={<QuarterlyDeckPublicPage />} />

      {/* ── Seertech Mirror — separate look & feel, /mirror/* ──
          Lives OUTSIDE DashboardLayout so Mirror keeps its own warm,
          journal-style chrome (cream + terracotta + Newsreader serif).
          Authentication is enforced via ProtectedRoute.
          Legacy `/me/*` paths below are kept as redirects so any
          previously-shared links continue to resolve. */}
      <Route path="/mirror" element={<ProtectedRoute><PIELandingPage /></ProtectedRoute>} />
      <Route path="/mirror/dashboard" element={<ProtectedRoute><PIEDashboardPage /></ProtectedRoute>} />
      <Route path="/mirror/improve" element={<ProtectedRoute><PIEImprovePage /></ProtectedRoute>} />
      <Route path="/mirror/simulate" element={<ProtectedRoute><PIESimulatorPage /></ProtectedRoute>} />
      <Route path="/mirror/validate" element={<ProtectedRoute><PIEValidatePage /></ProtectedRoute>} />
      <Route path="/mirror/coach" element={<ProtectedRoute><PIECoachPage /></ProtectedRoute>} />
      <Route path="/mirror/frontline" element={<ProtectedRoute><FrontlineDemoPage /></ProtectedRoute>} />
      <Route path="/mirror/frontline/tags" element={<ProtectedRoute><DemoTagsPage /></ProtectedRoute>} />
      <Route path="/mirror/frontline/remote/:code" element={<TourRemotePage />} />
      <Route path="/mirror/frontline/:persona" element={<ProtectedRoute><FrontlineDemoPage /></ProtectedRoute>} />
      <Route path="/mirror/frontline/:persona/:tab" element={<ProtectedRoute><FrontlineDemoPage /></ProtectedRoute>} />
      <Route path="/mirror/team" element={<ProtectedRoute><PIETeamPage /></ProtectedRoute>} />
      <Route path="/mirror/manager-pulse" element={<ProtectedRoute><PIEManagerPulsePage /></ProtectedRoute>} />
      <Route path="/mirror/calibration" element={<ProtectedRoute><PIECalibrationReviewPage /></ProtectedRoute>} />
      <Route path="/mirror/pulses" element={<ProtectedRoute><PIEPulseHistoryPage /></ProtectedRoute>} />

      {/* Legacy redirects — `/me/*` → `/mirror/*`. */}
      <Route path="/me" element={<Navigate to="/mirror" replace />} />
      <Route path="/me/dashboard" element={<Navigate to="/mirror/dashboard" replace />} />
      <Route path="/me/improve" element={<Navigate to="/mirror/improve" replace />} />
      <Route path="/me/simulate" element={<Navigate to="/mirror/simulate" replace />} />
      <Route path="/me/validate" element={<Navigate to="/mirror/validate" replace />} />
      <Route path="/me/coach" element={<Navigate to="/mirror/coach" replace />} />
      <Route path="/me/team" element={<Navigate to="/mirror/team" replace />} />
      <Route path="/me/manager-pulse" element={<Navigate to="/mirror/manager-pulse" replace />} />
      <Route path="/me/calibration" element={<Navigate to="/mirror/calibration" replace />} />
      <Route path="/me/pulses" element={<Navigate to="/mirror/pulses" replace />} />
      
      {/* Onboarding route (outside DashboardLayout) */}
      <Route path="/onboarding" element={<OnboardingRoute />} />

      {/* Forced password rotation (admin-invited accounts) — sits
          OUTSIDE the protected DashboardLayout so the redirect from
          ProtectedRoute lands here cleanly without flashing the app
          chrome. The page itself enforces auth and redirects to
          /login when unauthenticated. */}
      <Route path="/change-password" element={<ChangePasswordPage />} />

      {/* Self-service password reset — public token-based flow.
          Both pages are intentionally outside the protected layout
          so they're reachable while signed out. */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Standalone UX exploration — outside DashboardLayout so the
          sidebar/header chrome doesn't compete with the previews. */}
      <Route path="/lie-style-preview" element={<ProtectedRoute><LIEStylePreviewPage /></ProtectedRoute>} />
      
      {/* Protected routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<LearningIntelligenceEngine />} />
        <Route path="impact-modelling" element={<Dashboard />} />
        <Route path="configure" element={<ConfigurationPage />} />
        <Route path="portfolio" element={<PortfolioAnalysisPage />} />
        <Route path="matrix" element={<FullMatrixPage />} />
        <Route path="results" element={<ResultsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="import" element={<ImportPage />} />
        <Route path="scenarios" element={<ScenariosPage />} />
        <Route path="scenarios/:id" element={<ScenariosPage />} />
        <Route path="lei-reference" element={<LEIReferencePage />} />
        <Route path="compare" element={<CompareScenarios />} />
        <Route path="dampening-compare" element={<DampeningComparisonPage />} />
        <Route path="comparison-analytics" element={<ComparisonAnalyticsPage />} />
        <Route path="goals" element={<GoalsPage />} />
        <Route path="custom-measures" element={<CustomMeasuresPage />} />
        <Route path="scheduled-reports" element={<ScheduledReportsPage />} />
        <Route path="narrative-reports" element={<NarrativeReportsPage />} />
        <Route path="board-ready/:scenarioId" element={<BoardReadyPage />} />
        <Route path="accuracy" element={<AccuracyLeaderboardPage />} />
        <Route path="prospectus" element={<ProspectusListPage />} />
        <Route path="prospectus/new" element={<ProspectusBuilderPage />} />
        <Route path="prospectus/compare" element={<ProspectusComparePage />} />
        <Route path="prospectus/:prospectusId" element={<ProspectusBuilderPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="data-management" element={<DataManagementPage />} />
        <Route path="data-hub" element={<DataManagementHub />} />
        <Route path="benchmark-baseline" element={<BenchmarkBaselinePage />} />
        <Route path="gap-analysis" element={<GapAnalysisPage />} />
        <Route path="solution-finder" element={<SolutionFinderPage />} />
        <Route path="lei-dashboard" element={<LEIDashboardPage />} />
        <Route path="business-case-builder" element={<BusinessCaseBuilderPage />} />
        <Route path="usage-analytics" element={<UsageAnalyticsPage />} />
        <Route path="research" element={<ResearchEvidencePage />} />
        <Route path="component-data-import" element={<ComponentDataImportPage />} />
        <Route path="segments" element={<SegmentsPage />} />
        <Route path="program-analytics" element={<ProgramAnalyticsPage />} />
        <Route path="benchmarking" element={<BenchmarkingPage />} />
                <Route path="org-benchmarks" element={<OrgBenchmarksPage />} />
        <Route path="benchmarks-reference" element={<BenchmarksReferencePage />} />
        <Route path="settings/notifications" element={<NotificationPreferencesPage />} />
        <Route path="notifications" element={<NotificationCenterPage />} />
        <Route path="alert-rules" element={<AlertRulesPage />} />
        <Route path="lei-health/*" element={<LEIHealthDashboardPage />} />
        <Route path="score-trends" element={<ScoreTrendsPage />} />
        <Route path="people-engine" element={<PEHealthPage />} />
        <Route path="people-engine/health" element={<PEHealthPage />} />
        <Route path="people-engine/overview" element={<PEHealthPage />} />
        <Route path="people-engine/deep-dives" element={<PEHealthPage />} />
        <Route path="people-engine/trends" element={<PEHealthPage />} />
        <Route path="board-brief" element={<BoardBriefPage />} />
        <Route path="people-engine/inventory" element={<PEHealthPage />} />
        <Route path="people-engine/coverage" element={<PEHealthPage />} />
        <Route path="capability-inventory" element={<CapabilityInventoryPage />} />
        <Route path="people-engine/diagnostic" element={<PEDiagnosticPage />} />
        <Route path="people-engine/goals" element={<PEGoalsPage />} />
        <Route path="people-engine/intervention-backlog" element={<InterventionBacklogPage />} />
        <Route path="people-engine/benchmarking" element={<PEBenchmarkingPage />} />
        <Route path="people-engine/reference" element={<PEReferencePage />} />
        <Route path="correlation-analysis" element={<CorrelationAnalysisPage />} />
        <Route path="maturity-health/*" element={<MaturityHealthPage />} />
        <Route path="maturity-reference" element={<MaturityReferencePage />} />
        <Route path="roe-health/*" element={<ROEHealthPage />} />
        <Route path="performance-health/*" element={<PerformanceHealthPage />} />
        <Route path="roi-health/*" element={<ROIHealthPage />} />
        <Route path="story-guide" element={<StoryGuidePage />} />
        <Route path="anomaly-dashboard" element={<AnomalyDashboardPage />} />
        <Route path="roe-modeller" element={<ROEModellerPage />} />
        <Route path="roe-reference" element={<ROEReferencePage />} />
        <Route path="transfer-methodology" element={<TransferMethodologyPage />} />
        <Route path="admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
        <Route path="admin/telemetry" element={<AdminRoute><AdminTelemetryPage /></AdminRoute>} />
        <Route path="admin/data-health" element={<SuperAdminRoute><DataHealthPage /></SuperAdminRoute>} />
        <Route path="admin/code-health" element={<SuperAdminRoute><CodeHealthPage /></SuperAdminRoute>} />
        <Route path="admin/email-suppressions" element={<SuperAdminRoute><EmailSuppressionsAdminPage /></SuperAdminRoute>} />
        <Route path="admin/collateral" element={<SuperAdminRoute><CollateralLibraryPage /></SuperAdminRoute>} />
        <Route path="admin/industry-library" element={<SuperAdminRoute><IndustryLibraryPage /></SuperAdminRoute>} />
        <Route path="admin/coefficient-history" element={<AdminRoute><CoefficientHistoryPage /></AdminRoute>} />
        <Route path="admin/onboarding" element={<SuperAdminRoute><OnboardingListPage /></SuperAdminRoute>} />
        <Route path="admin/onboarding/:orgId" element={<AdminRoute><OnboardingWizardPage /></AdminRoute>} />
        <Route path="onboarding/share/:token" element={<OnboardingWizardPage />} />
        <Route path="admin/release-notes" element={<SuperAdminRoute><ReleaseNotesAdminPage /></SuperAdminRoute>} />
        <Route path="admin/reports/builder" element={<AdminRoute><AIReportBuilderPage /></AdminRoute>} />
        <Route path="admin/reports/builder/:reportId" element={<AdminRoute><AIReportBuilderPage /></AdminRoute>} />
        <Route path="admin/glossary" element={<SuperAdminRoute><AdminGlossaryPage /></SuperAdminRoute>} />
        <Route path="admin/messaging" element={<SuperAdminRoute><AdminMessagingPage /></SuperAdminRoute>} />
        <Route path="configuration" element={<Navigate to="/configure" replace />} />
        <Route path="full-matrix" element={<Navigate to="/matrix" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <AppProvider>
            <StorybookStatusProvider>
              <AppRoutes />
            </StorybookStatusProvider>
          </AppProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
